import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { usePageAutoCompleteApi } from '../../hooks/usePageAutoCompleteApi';

const AutoComplete = ({ label, value = '', allowedLocations, onSelect, placeholder, debounceInput }) => {
  const [debouncedInput, setDebouncedInput] = useState(debounceInput || true)
  const { getAutoComplete, loading } = usePageAutoCompleteApi();
  const [inputValue, setInputValue] = useState(value || '');
  const [data, setData] = useState(null);
  const [focused, setFocused] = useState(false);
  const [isSelected, setIsSelected] = useState(false)
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const getAutoCompleteRef = useRef(getAutoComplete);

  const handleInputBlur = () => {
    document.addEventListener('click', (event) => {
      if (listRef.current && !listRef.current.contains(event.target) && inputRef.current !== event.target) {
        setFocused(false);
      }
    }, { once: true });
  };

  const handleInputFocus = () => {
    setFocused(true);
  };

  useEffect(() => {
    if ((value !== inputValue && inputValue) || (value && inputValue == '')) {
      setInputValue(value)
    }
  }, [value] )

  const handleChange = (e) => {
    setIsSelected(false)

    if (e.target.value.length == 0) {
      setInputValue(e.target.value);
      setFocused(true);
    } else {
      setDebouncedInput(true)
      setInputValue(e.target.value);
    }
  };

  const handleSelect = (item) => {
    setIsSelected(true)
    onSelect(item);
    setInputValue(item.formatted_address?.split('<br>')[0] || '');
    setFocused(false);
  };

  const renderList = (listData) => listData ? (
    <div
      className="absolute w-full bg-[#F8F9FB] top-2 rounded-3xl shadow-md mt-1 max-h-60 z-10 overflow-hidden"
      ref={listRef}
    >
      <div className="h-[62px] rounded-full border-b" />
      <div className="max-h-60 overflow-auto pb-16">
        <ul>
          {listData.map((item, index) => (
            <li key={index} onClick={() => handleSelect(item)}
                className="px-6 py-3 pl-8 hover:bg-gray-100 cursor-pointer flex">
                  <img src={item.icon_url} width="22" height="22" className="h-[22px] mr-2 flex-none"></img>
              <div className="flex-initial">{item.formatted_address?.split('<br>')[0]}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    getAutoCompleteRef.current = getAutoComplete;
  }, [getAutoComplete]);

  const debouncedGetAutoComplete = useCallback(
    debounce(async (value) => {
      if (value.length > 2) {
        try {
          const results = await getAutoCompleteRef.current(value);
          setData(results.data);
        } catch (error) {
          console.error('Failed to fetch autocomplete data:', error);
        }
      }
    }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if (inputValue && !isSelected && debouncedInput) {
      debouncedGetAutoComplete(inputValue);
    }
  }, [inputValue, debouncedGetAutoComplete]);

  return (
    <div className="w-full px-4 py-2">
      <div className="relative text-left rounded-full bg-[#F8F9FB] bg-opacity-80 h-[71px]">
        <div className="px-3 py-3 border-b rounded-full">
          <label htmlFor="autoComplete" className={`
            relative font-bold text-[#1C2938] pl-4 mb-2 ${focused ? 'z-20' : ''}
          `}>
            {label}
          </label>
          <input
            ref={inputRef}
            type="text"
            id="autoComplete"
            value={inputValue}
            className={`
              relative w-full pl-4 text-sm font-normal text-[#1C2938] bg-transparent
              focus:outline-none rounded-full ${focused ? 'z-20' : ''}
            `}
            placeholder={placeholder}
            autoComplete="off"
            onChange={handleChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
          {loading && (
            <div className="absolute right-9 top-8">
              <div className={`
                w-6 h-6 border-2 border-[#4FAB55] border-t-transparent rounded-full animate-spin
                relative ${focused ? 'z-20' : ''}
              `}
              />
            </div>
          )}
        </div>
        {focused && renderList(allowedLocations || data)}
      </div>
    </div>
  );
};

AutoComplete.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  allowedLocations: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  debounceInput: PropTypes.bool
};

export default AutoComplete;
